import { useState } from "react";
import { Alert, Button, Modal, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { GetDeletionImpact, RequestAccountDeletion } from "../../redux/auth-reducer/auth-service";
import { REMEMBERED_EMAIL_KEY, forceLogout, readApiMessage } from "../../utils/helper/helper";
import { clearCheckoutIntent } from "../../utils/helper/checkout";
import { deletionRequestedMessage, formatLocalDate } from "../../utils/helper/accountDeletion";

/** The warnings the tickets prescribe for the two lists (#199 AC2, AC3). */
export const AUCTIONS_WARNING =
  "If you delete your account now, you will not be able to receive a prize if you win.";
export const SECONDARY_MARKET_WARNING =
  "If your account is deleted and your bid wins, no payment will be expected.";

/** #234: after the request, on the sign-in page, under the deletion date. */
export const DELETION_SIGNED_OUT_NOTE =
  "You have been signed out. To keep your account, sign in before then and cancel the deletion.";

/**
 * The steps the player goes through: the overview always, then each warning that has rows.
 *
 * @param {object|null} impact The data of Player/GetDeletionImpact.
 * @returns {string[]} Some of "overview", "auctions", "secondary", in that order.
 */
export const deletionSteps = (impact) => [
  "overview",
  ...(impact?.activeAuctions?.length ? ["auctions"] : []),
  ...(impact?.secondaryMarket?.length ? ["secondary"] : []),
];

const auctionReason = (row) => {
  if (row.reason === "Holding") return "You own a share";
  return row.isLeading ? "Your bid is leading" : "You have bid";
};

const secondaryMarketRole = (row) => {
  if (row.kind === "Listing") return "You are selling";
  return row.isLeading ? "Your bid is leading" : "You have bid";
};

const money = (value) => (typeof value === "number" ? `$${value.toFixed(2)}` : "-");

/**
 * Account Settings' "Delete Account" (#199). Nothing is requested until the last step's button.
 *
 * Steps: an overview - when the account will be deleted, the subscription and what is lost - and
 * then, each only when it has rows, the auctions and contests the player takes part in (AC2) and
 * their secondary-market bids and listings (AC3). The last step shown sends the request.
 *
 * On success the server has already ended the session, so the player is signed out at once -
 * forceLogout without a revoke, which also beats any in-flight request whose refused refresh would
 * otherwise sign out with the generic message. The deletion date is shown on the sign-in page as an
 * informational notice, with how to keep the account: signing in before then offers to cancel the
 * deletion (#234). With no reply, or a refusal, the dialog stays open on its last step.
 *
 * Every date is shown in the player's timezone (formatLocalDate), as elsewhere in the app.
 */
export const DeleteAccount = () => {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [impact, setImpact] = useState(null);
  const [step, setStep] = useState(null);

  const steps = deletionSteps(impact);
  const isLastStep = steps.indexOf(step) === steps.length - 1;

  const close = () => {
    setStep(null);
    setImpact(null);
  };

  const open = async () => {
    setLoading(true);
    const res = await GetDeletionImpact();
    setLoading(false);

    if (res?.data?.status === true && res.data.data) {
      setImpact(res.data.data);
      setStep("overview");
      return;
    }

    if (res) toast.error(readApiMessage(res));
  };

  const submit = async () => {
    setBusy(true);
    const res = await RequestAccountDeletion();

    if (res?.data?.status === true) {
      // The account can no longer sign in: nothing should pre-fill it, or resume a purchase for it.
      localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      clearCheckoutIntent();
      // Worded here, with the date in the player's timezone; the server's message has the UTC day.
      const confirmation = res.data.deletionDate
        ? deletionRequestedMessage(res.data.deletionDate)
        : res.data.message;
      forceLogout(
        { type: "info", text: `${confirmation} ${DELETION_SIGNED_OUT_NOTE}` },
        { returnHere: false }
      );
      return;
    }

    setBusy(false);
    if (res) toast.error(readApiMessage(res));
  };

  const next = () => {
    if (isLastStep) {
      submit();
      return;
    }
    setStep(steps[steps.indexOf(step) + 1]);
  };

  const proceedLabel = () => {
    if (busy) return "Deleting...";
    if (step === "overview") return isLastStep ? "Delete Account" : "Continue";
    return "Delete anyway";
  };

  const titles = {
    overview: "Delete Account",
    auctions: "Active Auctions and Contests",
    secondary: "Active Secondary Market Bids",
  };

  return (
    <div className="table-transaction mb-4">
      <h1 className="subscription_heading">Delete Account</h1>
      <p>
        Deleting your account removes your name, contact details and picture from Xcutta. You are
        signed out as soon as you ask for it and cannot use your account until it is deleted. To keep
        it, sign in before the deletion date and cancel the deletion.
      </p>
      <Button variant="outline-danger" onClick={open} disabled={loading || step !== null}>
        {loading ? "Loading..." : "Delete Account"}
      </Button>

      <Modal
        show={step !== null}
        centered
        size={step === "overview" ? undefined : "lg"}
        onHide={busy ? undefined : close}
        backdrop={busy ? "static" : true}
        keyboard={!busy}
      >
        <Modal.Header className="promo-code-header promo-close" closeButton={!busy}>
          <Modal.Title>
            <b>{titles[step]}</b>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {step === "overview" && impact && (
            <>
              <p>
                Your account will be deleted on <b>{formatLocalDate(impact.deletionDate)}</b>. Until then
                it stays closed: you will be signed out now and cannot use it. To keep your account,
                sign in before that date and cancel the deletion.
              </p>
              <p>
                Your personal details will be removed. Past results stay on Xcutta, shown as
                &quot;Deleted User&quot;.
              </p>
              {impact.hasActiveSubscription && (
                <Alert variant="warning">
                  Your subscription is active until{" "}
                  <b>{formatLocalDate(impact.subscriptionEndsAt)}</b>. It will not be refunded, and its
                  auto-renewal is turned off when you request deletion. Cancelling the deletion does
                  not turn it back on.
                </Alert>
              )}
              {impact.auctionPassCredits > 0 && (
                <Alert variant="warning">
                  You have {impact.auctionPassCredits} unused auction pass credit
                  {impact.auctionPassCredits === 1 ? "" : "s"}, which will be lost.
                </Alert>
              )}
            </>
          )}

          {step === "auctions" && impact && (
            <>
              <Alert variant="danger">{AUCTIONS_WARNING}</Alert>
              <div className="manager-confirm-list">
                <Table size="sm" bordered>
                  <thead>
                    <tr>
                      <th>Auction</th>
                      <th>Contest</th>
                      <th>Team</th>
                      <th>Ends</th>
                      <th>Your part</th>
                    </tr>
                  </thead>
                  <tbody>
                    {impact.activeAuctions.map((row) => (
                      <tr key={`${row.auctionId}-${row.teamName}-${row.reason}`}>
                        <td>{row.auctionName}</td>
                        <td>{row.contestName}</td>
                        <td>{row.teamName}</td>
                        <td>{formatLocalDate(row.contestEndDate)}</td>
                        <td>{auctionReason(row)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}

          {step === "secondary" && impact && (
            <>
              <Alert variant="danger">{SECONDARY_MARKET_WARNING}</Alert>
              <div className="manager-confirm-list">
                <Table size="sm" bordered>
                  <thead>
                    <tr>
                      <th>Auction</th>
                      <th>Team</th>
                      <th>Share</th>
                      <th>Your bid</th>
                      <th>Top bid</th>
                      <th>Status</th>
                      <th>Your part</th>
                    </tr>
                  </thead>
                  <tbody>
                    {impact.secondaryMarket.map((row) => (
                      <tr key={`${row.kind}-${row.auctionId}-${row.teamName}-${row.auctionLevel}`}>
                        <td>{row.auctionName}</td>
                        <td>{row.teamName}</td>
                        <td>{row.shareOffered}%</td>
                        <td>{money(row.myBidAmount)}</td>
                        <td>{money(row.topBidAmount)}</td>
                        <td>{row.listingStatus}</td>
                        <td>{secondaryMarketRole(row)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              {impact.secondaryMarket.some((row) => row.kind === "Listing") && (
                <p className="mb-0">Listings you are selling stay open and can still sell.</p>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={close} disabled={busy}>
            {step === "overview" ? "Cancel" : "Cancel deletion"}
          </Button>
          <Button variant="danger" onClick={next} disabled={busy}>
            {proceedLabel()}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
